import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  AppDialog,
  AppDialogContent,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogDescription,
  AppDialogBody,
  AppFilterChipSelector,
  AppTextInput,
  AppTypography,
} from '@easyllama/ui-components';
import ShortcutList from './molecules/ShortcutList';
import TypeFilterBar from './molecules/TypeFilterBar';
import GlobalSearchResultGroup from './molecules/GlobalSearchResultGroup';
import { ShortcutListSkeleton, SkeletonRows } from './molecules/Skeletons';
import {
  SEARCH_DEBOUNCE_MS,
  SKELETON_DELAY_MS,
  MIN_TERM_LENGTH,
  MAX_ROWS_PER_GROUP,
  SURFACE_TOKENS,
  SHORTCUT_HOTKEYS,
} from './constants';

function GlobalSearchDialog({ open, onOpenChange, searchUrl }) {
  const [term, setTerm] = useState('');
  const [status, setStatus] = useState('idle');
  const [groups, setGroups] = useState([]);
  const [shortcuts, setShortcuts] = useState([]);
  const [shortcutsStatus, setShortcutsStatus] = useState('loading');
  const [activeShortcut, setActiveShortcut] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasSearchedOnce, setHasSearchedOnce] = useState(false);
  const rowRefs = useRef([]);
  const inputRef = useRef(null);
  const shortcutAbortRef = useRef(null);

  useEffect(() => () => shortcutAbortRef.current?.abort(), []);

  const trimmedTerm = term.trim();
  const isSearching = trimmedTerm.length >= MIN_TERM_LENGTH;

  useEffect(() => {
    if (open) return;
    shortcutAbortRef.current?.abort();
    setTerm('');
    setStatus('idle');
    setGroups([]);
    setShortcuts([]);
    setShortcutsStatus('loading');
    setActiveShortcut(null);
    setSelectedTypes(null);
    setActiveIndex(0);
    setHasSearchedOnce(false);
  }, [open]);

  const request = useCallback(async (query, { signal } = {}) => {
    const response = await fetch(`${searchUrl}${query}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal,
    });
    if (!response.ok) throw new Error('Global search request failed');
    return response.json();
  }, [searchUrl]);

  useEffect(() => {
    if (!open || !searchUrl) return undefined;

    const controller = new AbortController();
    (async () => {
      try {
        const data = await request('', { signal: controller.signal });
        if (controller.signal.aborted) return;
        setShortcuts(data.shortcuts || []);
        setShortcutsStatus('loaded');
      } catch (error) {
        // Shortcuts are optional; clear the loading state even on failure.
        if (error.name !== 'AbortError' && !controller.signal.aborted) setShortcutsStatus('loaded');
      }
    })();

    return () => controller.abort();
  }, [open, searchUrl, request]);

  useEffect(() => {
    if (!open || !searchUrl) return undefined;

    // Typing (even below MIN_TERM_LENGTH) supersedes a running shortcut fetch.
    shortcutAbortRef.current?.abort();

    if (!isSearching) {
      setStatus('idle');
      setGroups([]);
      setSelectedTypes(null);
      setActiveShortcut(null);
      return undefined;
    }

    const controller = new AbortController();
    let skeletonTimeout;
    const debounceTimeout = setTimeout(async () => {
      skeletonTimeout = setTimeout(() => setStatus('loading'), SKELETON_DELAY_MS);
      try {
        const data = await request(`?term=${encodeURIComponent(trimmedTerm)}`, { signal: controller.signal });
        if (controller.signal.aborted) return;
        clearTimeout(skeletonTimeout);
        // Cleared alongside the new groups so the old filter never flashes over unfiltered hits.
        setSelectedTypes(null);
        setActiveShortcut(null);
        setGroups(data.groups || []);
        setStatus('success');
        setHasSearchedOnce(true);
      } catch (error) {
        if (error.name === 'AbortError' || controller.signal.aborted) return;
        clearTimeout(skeletonTimeout);
        setGroups([]);
        setSelectedTypes(null);
        setActiveShortcut(null);
        setStatus('error');
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(debounceTimeout);
      clearTimeout(skeletonTimeout);
      controller.abort();
    };
  }, [trimmedTerm, isSearching, open, searchUrl, request]);

  const runShortcut = useCallback(async (shortcut) => {
    shortcutAbortRef.current?.abort();
    const controller = new AbortController();
    shortcutAbortRef.current = controller;

    setSelectedTypes(null);
    // Same skeleton delay as the term-search flow, so a fast response never flashes it; the pinned list stays up until then.
    const skeletonTimeout = setTimeout(() => {
      if (controller.signal.aborted) return;
      setActiveShortcut(shortcut);
      setStatus('loading');
    }, SKELETON_DELAY_MS);
    try {
      const data = await request(`?shortcut=${encodeURIComponent(shortcut.key)}`, { signal: controller.signal });
      // abort() doesn't cancel an already-settled fetch, so guard against a stale payload too.
      if (controller.signal.aborted) return;
      setActiveShortcut(shortcut);
      setGroups(data.groups || []);
      setStatus('success');
    } catch (error) {
      if (error.name === 'AbortError' || controller.signal.aborted) return;
      setActiveShortcut(shortcut);
      setStatus('error');
    } finally {
      clearTimeout(skeletonTimeout);
    }
  }, [request]);

  function clearShortcut() {
    shortcutAbortRef.current?.abort();
    setActiveShortcut(null);
    setGroups([]);
    setStatus('idle');
    inputRef.current?.focus();
  }

  const visibleGroups = useMemo(
    () => (selectedTypes ? groups.filter((group) => selectedTypes.has(group.key)) : groups),
    [groups, selectedTypes]
  );

  const groupsWithOffsets = useMemo(() => {
    let runningIndex = 0;
    return visibleGroups.map((group) => {
      const startIndex = runningIndex;
      runningIndex += Math.min(group.rows.length, MAX_ROWS_PER_GROUP);
      return { group, startIndex };
    });
  }, [visibleGroups]);

  const flatRows = useMemo(
    () => visibleGroups.flatMap((group) => group.rows.slice(0, MAX_ROWS_PER_GROUP)),
    [visibleGroups]
  );

  const showShortcuts = !isSearching && !activeShortcut && shortcuts.length > 0;
  const showShortcutsSkeleton = !isSearching && !activeShortcut && shortcutsStatus === 'loading';
  const navigableCount = showShortcuts ? shortcuts.length : status === 'success' ? flatRows.length : 0;

  useEffect(() => {
    setActiveIndex(0);
  }, [visibleGroups, showShortcuts]);

  useEffect(() => {
    rowRefs.current[activeIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  function toggleType(key) {
    setSelectedTypes((previous) => {
      const next = new Set(previous ?? []);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next.size === 0 ? null : next;
    });
  }

  function openUrl(url, newTab) {
    if (newTab) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    onOpenChange(false);
    window.location.assign(url);
  }

  function handleKeyDown(event) {
    // An IME commits its composition with Enter too; read isComposing off the native event.
    if (event.key === 'Enter' && (event.nativeEvent.isComposing || event.keyCode === 229)) return;

    // Only while the box is untouched - once typing starts, a digit is query text (e.g. "H2S").
    if (showShortcuts && term === '' && SHORTCUT_HOTKEYS.includes(event.key) && !event.metaKey && !event.ctrlKey && !event.altKey) {
      const shortcut = shortcuts[SHORTCUT_HOTKEYS.indexOf(event.key)];
      if (!shortcut) return;
      event.preventDefault();
      runShortcut(shortcut);
      return;
    }

    if (navigableCount === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, navigableCount - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      if (showShortcuts) {
        const shortcut = shortcuts[activeIndex];
        if (!shortcut) return;
        event.preventDefault();
        if (event.metaKey || event.ctrlKey) {
          openUrl(shortcut.url, true);
        } else {
          runShortcut(shortcut);
        }
        return;
      }
      const row = flatRows[activeIndex];
      if (!row?.url) return;
      event.preventDefault();
      openUrl(row.url, event.metaKey || event.ctrlKey);
    }
  }

  const showSkeleton = status === 'loading';
  const showFilterRow = Boolean(activeShortcut)
    || (isSearching && (groups.length > 0 || (status === 'loading' && !hasSearchedOnce)));
  const resultCount = flatRows.length;
  const viewKey = showShortcutsSkeleton ? 'shortcuts-skeleton' : showShortcuts ? 'shortcuts' : showSkeleton ? 'skeleton' : status;
  const isBusy = showSkeleton || showShortcutsSkeleton;

  return (
    <AppDialog open={open} onOpenChange={onOpenChange}>
      <AppDialogContent
        size="lg"
        showCloseButton
        style={SURFACE_TOKENS}
        // Above the mobile sidebar shell (z-index 1010) and DrawerGradientShadow's z-60 glow, so nothing overlaps it.
        overlayClassName="!z-[1020]"
        // Forced on every descendant - the library's own leaf components set their own font-family.
        className="global-search-dialog-preview !z-[1020] !top-(--gs-surface-offset) !max-h-(--gs-surface-height) !max-w-(--gs-surface-width) !translate-y-0 !gap-0 !overflow-hidden !rounded-(--el-radius-rounded-md) !p-0 !font-[Raleway,sans-serif] [&_*]:!font-[Raleway,sans-serif]"
      >
        <div
          className={clsx(
            'flex h-(--gs-input-height) items-center gap-3 px-(--gs-gutter)',
            !showFilterRow && 'border-b border-llama-grey-3',
          )}
        >
          {/* AppTextInput has no right-icon slot, so the pulse ring is pinned to the wrapper edge. */}
          <div className="relative min-w-0 flex-1">
            <AppTextInput
              ref={inputRef}
              type="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search for learners, training courses, locations, etc."
              aria-label="Search"
              autoFocus
              wrapperClassName="w-full [&_svg]:!size-(--gs-input-icon-size) [&>div>span:first-child]:!left-0"
              className="!h-(--gs-input-height) !border-0 !bg-transparent !pl-(--gs-input-icon-gap) !text-[length:var(--gs-input-font)] !font-medium !shadow-none placeholder:!text-[length:var(--gs-input-font)] placeholder:!font-normal focus:!ring-0"
            />
            {isBusy && (
              <span
                data-testid="global-search-pulse"
                aria-hidden="true"
                className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin rounded-full border-2 border-llama-purple-1 border-t-llama-purple-6"
              />
            )}
          </div>
          <AppDialogHeader className="!flex-none !items-center !gap-0 !pb-0">
            <AppDialogTitle className="sr-only">Search</AppDialogTitle>
            <AppDialogDescription className="sr-only">
              Search learners, trainings, classrooms, journeys, locations, groups, documents and kiosk links
            </AppDialogDescription>
          </AppDialogHeader>
        </div>

        {showFilterRow && (
          <div className="border-b border-llama-grey-3 px-(--gs-gutter) pb-3">
            {activeShortcut ? (
              <AppFilterChipSelector label={activeShortcut.title} active onRemove={clearShortcut} size="small" />
            ) : (
              <TypeFilterBar
                groups={groups}
                selectedTypes={selectedTypes}
                onToggleType={toggleType}
                onSelectAll={() => setSelectedTypes(null)}
                showPillSkeleton={status === 'loading' && !hasSearchedOnce}
              />
            )}
          </div>
        )}

        <AppDialogBody className="flex min-h-0 flex-1 flex-col !p-0">
          <div className="min-h-0 flex-1 overflow-y-auto px-(--gs-gutter) py-3">
            {/* Keyed to remount and re-fade on every state swap instead of cutting instantly. */}
            <div key={viewKey} className="animate-in fade-in-0 duration-150">
              {showShortcutsSkeleton && <ShortcutListSkeleton />}
              {showShortcuts && (
                <ShortcutList
                  shortcuts={shortcuts}
                  activeIndex={activeIndex}
                  onHover={setActiveIndex}
                  onRun={runShortcut}
                  rowRefs={rowRefs}
                />
              )}
              {showSkeleton && <SkeletonRows />}
              {status === 'error' && (
                <AppTypography as="p" variant="body-14" className="py-6 text-center text-llama-grey-5">
                  Something went wrong. Try searching again.
                </AppTypography>
              )}
              {status === 'success' && visibleGroups.length === 0 && (
                <AppTypography as="p" variant="body-14" className="py-6 text-center text-llama-grey-5">
                  {activeShortcut ? `Nothing to show for "${activeShortcut.title}".` : `No results found for "${trimmedTerm}".`}
                </AppTypography>
              )}
              {status === 'success' && visibleGroups.length > 0 && (
                <div className="space-y-6">
                  {groupsWithOffsets.map(({ group, startIndex }) => (
                    <GlobalSearchResultGroup
                      key={group.key}
                      group={group}
                      term={trimmedTerm}
                      startIndex={startIndex}
                      activeIndex={activeIndex}
                      onRowHover={setActiveIndex}
                      rowRefs={rowRefs}
                      onNavigate={() => onOpenChange(false)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </AppDialogBody>

        <div className="flex h-(--gs-footer-height) shrink-0 items-center justify-between border-t border-llama-grey-3 bg-llama-purple-0 px-(--gs-gutter) text-xs text-llama-grey-5">
          {status === 'idle' && showShortcutsSkeleton && <span>Loading…</span>}
          {status === 'idle' && !showShortcutsSkeleton && (
            <span>Start typing to search learners, trainings, classrooms, journeys, locations, groups, documents and kiosk links.</span>
          )}
          {status === 'loading' && <span>Searching…</span>}
          {(status === 'success' || status === 'error') && (
            <>
              <span className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="rounded border border-llama-grey-3 bg-white px-1 font-mono text-[length:var(--gs-label-font)]">↑↓</kbd>
                  Navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="rounded border border-llama-grey-3 bg-white px-1 font-mono text-[length:var(--gs-label-font)]">⌘↵</kbd>
                  Open in new tab
                </span>
              </span>
              {status === 'success' && (
                <span>
                  {activeShortcut ? `${resultCount} in ${activeShortcut.title}` : `${resultCount} results for "${trimmedTerm}"`}
                </span>
              )}
            </>
          )}
        </div>
      </AppDialogContent>
    </AppDialog>
  );
}

GlobalSearchDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  searchUrl: PropTypes.string,
};

export default GlobalSearchDialog;
